/**
 * Klient HTTP dla tego, co musi zadziałać przed WebSocketem albo ustawić
 * ciasteczko: logowanie, rejestracja z zaproszenia, lista kont, akcje admina.
 * Cała rozgrywka idzie po WebSocket.
 */

import type { Akcent } from "@gierki/shared";

export interface ApiUser {
  id: string;
  nick: string;
  isAdmin: boolean;
  kolor: Akcent;
}

/** Wiersz bilansu: jeden gracz × jedna gra, patrz `server/src/stats/repository.ts` `bilans()`. */
export interface BilansWiersz {
  userId: string;
  nick: string;
  gameType: string;
  rozegrane: number;
  wygrane: number;
  najlepszy: number;
}

/**
 * Rekord wspólny — najlepsza wartość faktu (`kod`) w całej historii partii.
 * `userId`/`nick` bywają `null`: `LEFT JOIN` na wypadek konta usuniętego po
 * ustawieniu rekordu.
 */
export interface Rekord {
  kod: string;
  liczba: number;
  tekst: string | null;
  userId: string | null;
  nick: string | null;
  gameId: string;
  /** Gra, z której pochodzi fakt — po niej grupowana jest lista rekordow. */
  gameType: string;
  finishedAt: number;
}

export interface WynikGraczaHistoria {
  userId: string;
  nick: string;
  score: number;
  pozycja: number;
}

export interface PartiaWiersz {
  gameId: string;
  gameType: string;
  finishedAt: number;
  wyniki: WynikGraczaHistoria[];
}

export interface FaktWiersz {
  kod: string;
  liczba: number | null;
  tekst: string | null;
  userId: string | null;
  nick: string | null;
}

export interface ZdarzenieWiersz {
  id: string;
  at: number;
  typ: string;
  payload: unknown;
}

export interface PartiaSzczegoly extends PartiaWiersz {
  fakty: FaktWiersz[];
  zdarzenia: ZdarzenieWiersz[];
}

export interface ProfilPoGrach {
  gameType: string;
  rozegrane: number;
  wygrane: number;
  najlepszy: number;
}

/** Osobisty rekord gracza — ten sam kształt co `Rekord`, ale bez `userId`/`nick`: to już wiadomo, czyj profil się ogląda. */
export interface ProfilFakt {
  kod: string;
  liczba: number;
  tekst: string | null;
  gameId: string;
  gameType: string;
  finishedAt: number;
  /** W ilu zakonczonych partiach ten fakt padl i w ilu z nich wyszedl niezerowy. */
  partii: number;
  zaliczone: number;
}

export interface ProfilGracza {
  rozegrane: number;
  wygrane: number;
  poGrach: ProfilPoGrach[];
  fakty: ProfilFakt[];
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
    // Ciasteczko sesji musi jechać z każdym żądaniem — bez tego logowanie
    // działa, ale nic po nim nie działa.
    credentials: "same-origin",
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
  };

  if (!response.ok) {
    throw new ApiError(
      payload.message ?? "Coś poszło nie tak.",
      payload.error ?? "unknown",
      response.status,
    );
  }
  return payload as T;
}

export const api = {
  me: () => request<{ user: ApiUser }>("/api/auth/me"),

  login: (nick: string, password: string) =>
    request<{ user: ApiUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ nick, password }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  ustawKolor: (kolor: Akcent) =>
    request<{ user: ApiUser }>("/api/auth/kolor", {
      method: "POST",
      body: JSON.stringify({ kolor }),
    }),

  /** Co kryje się pod linkiem: rejestracja czy zmiana hasła. */
  describeInvite: (token: string) =>
    request<{ purpose: "register" | "password_reset"; targetNick: string | null }>(
      `/api/auth/invite/${encodeURIComponent(token)}`,
    ),

  register: (token: string, nick: string, password: string) =>
    request<{ user: ApiUser }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ token, nick, password }),
    }),

  resetPassword: (token: string, password: string) =>
    request<{ user: ApiUser }>("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  users: () => request<{ users: Array<{ id: string; nick: string }> }>("/api/users"),

  createInvite: (purpose: "register" | "password_reset", targetUserId: string | null = null) =>
    request<{ token: string; expiresAt: number }>("/api/admin/invite", {
      method: "POST",
      body: JSON.stringify({ purpose, targetUserId }),
    }),

  setUserActive: (userId: string, isActive: boolean) =>
    request<{ ok: true }>("/api/admin/user-active", {
      method: "POST",
      body: JSON.stringify({ userId, isActive }),
    }),

  setUserAdmin: (userId: string, isAdmin: boolean) =>
    request<{ ok: true }>("/api/admin/user-admin", {
      method: "POST",
      body: JSON.stringify({ userId, isAdmin }),
    }),

  /** Tylko w developmencie — na produkcji ta trasa nie istnieje. */
  devLogin: (nick: string) =>
    request<{ user: ApiUser }>("/api/dev/login", {
      method: "POST",
      body: JSON.stringify({ nick }),
    }),

  stats: () => request<{ bilans: BilansWiersz[]; rekordy: Rekord[] }>("/api/stats"),
  /** Czolowka jednego rekordu. Kod idzie parametrem zapytania, bo bywa z dwukropkiem. */
  statsRecord: (kod: string) =>
    request<{ top: Rekord[] }>(`/api/stats/records?kod=${encodeURIComponent(kod)}`),

  // Trasa zwraca tablicę wprost, nie `{ partie: [...] }` — `historia()`
  // w `service.ts` nie owija wyniku.
  statsGames: (limit = 30, before?: string) =>
    request<PartiaWiersz[]>(
      `/api/stats/games?limit=${limit}${before ? `&before=${before}` : ""}`,
    ),

  statsGame: (id: string) => request<PartiaSzczegoly>(`/api/stats/games/${id}`),

  statsPlayer: (id: string) => request<ProfilGracza>(`/api/stats/players/${id}`),
};
